// Cover image upload via the Firebase Storage SDK — used for host event
// covers and (from the admin app) club covers.
//
// The user is already Firebase-authenticated by AuthProvider when signed in,
// so uploadBytes will carry their auth token; Storage Rules then gate on
// request.auth.uid matching the path prefix. The returned download URL
// carries a per-object access token, so we don't need to make the bucket
// world-readable.
//
// Required Storage Rules on this bucket:
//
//   service firebase.storage {
//     match /b/{bucket}/o {
//       match /event-covers/{userId}/{file=**} {
//         allow read;
//         allow write: if request.auth != null && request.auth.uid == userId;
//       }
//     }
//   }
import { useMutation } from "@tanstack/react-query";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirebaseStorage, getCurrentFirebaseUser } from "@/lib/firebase";

const EXT_BY_MIME: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

function randomId(): string {
  // 16 hex chars, no UUID dependency
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function uploadEventCover(file: File): Promise<string> {
  const ext = EXT_BY_MIME[file.type];
  if (!ext) {
    throw new Error("Unsupported image format");
  }

  const user = getCurrentFirebaseUser();
  if (!user) {
    throw new Error("You need to be signed in to upload an image");
  }

  const path = `event-covers/${user.uid}/${randomId()}.${ext}`;
  const fileRef = ref(getFirebaseStorage(), path);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return getDownloadURL(fileRef);
}

export function useEventCoverUpload() {
  return useMutation({
    mutationFn: (file: File) => uploadEventCover(file),
  });
}
